import {Component} from '@angular/core';
import {FindProjectsForm} from './find-projects.model';
import {Project} from '../interfaces/project';
import {Router} from '@angular/router';
import firebase from 'firebase/compat/app';
import {UserService} from '../services/user.service';
import {TeacherService} from "../services/teacher.service";
import {Teacher} from "../interfaces/teacher";


@Component({
  selector: 'app-find-projects',
  templateUrl: './find-projects.component.html',
  styleUrl: './find-projects.component.css'
})
export class FindProjectsComponent {
  searchText = "";
  hoveredRow: number | null = null;

  constructor(private router: Router, private userService: UserService, private teacherService: TeacherService) {
  };

  findProjectsForm: FindProjectsForm = new FindProjectsForm();
  grades: string[] = ["Pre-K", "K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
  projects: Project[] = [];
  filteredProjects: Project[] = [];
  gradeDropdownText = "Select Grades";
  teacher: Teacher | null = null;
  downloadedTable = false;

  // right now this just happens whenever anything changes like search text or grades,
  // but might be better to have a dedicated filter button?
  filterResults() {
    const selectedGrades = this.findProjectsForm.selectedGrades;
    const searchText = this.searchText.toLowerCase();

    this.filteredProjects = this.projects.filter(project => {
      const matchesGrade = selectedGrades.length === 0 ||
        selectedGrades.some(grade => project.grades.includes(grade));

      const matchesText = project.title.toLowerCase().includes(searchText) ||
        project.school.toLowerCase().includes(searchText);

      return matchesGrade && matchesText;
    });
    this.downloadedTable = false;
    this.updatePagination();
  }

  onSearchTextChange() {
    this.filterResults();
  }

  onGradeChange(event: Event, grade: string) {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      this.findProjectsForm.selectedGrades.push(grade);
    } else {
      this.findProjectsForm.selectedGrades = this.findProjectsForm.selectedGrades.filter(
        selected => selected !== grade
      );
    }

    const selectedGradesLength = this.findProjectsForm.selectedGrades.length;
    if (selectedGradesLength > 0) {
      this.gradeDropdownText = selectedGradesLength + " Selected";
    } else {
      this.gradeDropdownText = "Select Grades"
    }

    this.filterResults();
  }

  isFetching = false;
  fetchQueue = 0;

  async getProjects() {
    const role = this.teacher?.role.toLowerCase();
    const school = this.teacher?.school.toLowerCase();

    if (this.isFetching) {
      this.fetchQueue++
      return;
    }

    this.isFetching = true;
    this.projects = [];
    let query;

    if (this.userService.getUID()) {
      query = firebase.firestore().collection('projects').where('visibility', 'in', ['Everyone', 'My School']);
    } else {
      query = firebase.firestore().collection('projects').where('visibility', '==', 'Everyone');
    }

    const snapshot = await query.get();
    snapshot.forEach((doc) => {
      const data = {id: doc.id, ...doc.data() as Project};
      if (!role) return;
      if (role === 'teacher' && school !== data.school.toLowerCase()) return;
      if (role === 'ghs teacher representative' && school != data.school.toLowerCase()) return;
      if (role !== 'admin' && data.isDeleted) return;

      this.projects.push(data as Project);
    });
    this.allProjects = [...this.projects];
    this.filteredProjects = [...this.projects];

    this.filterResults();
    this.applySorting();
    this.isFetching = false;
    if (this.fetchQueue > 0) {
      this.fetchQueue--;
      this.getProjects();
    }
    this.updatePagination();
  }

  onProjectCardClicked(id: any) {
    this.router.navigate(['project', id]);
  }

  async ngOnInit() {
    this.filteredProjects = this.projects;
    this.teacher = await this.teacherService.getTeacher();
    await this.getProjects();
    // Default newest first
    this.sorts = [{field: 'date', dir: 'desc'}];
    this.applySorting();
  }

  // PAGINATION
  cardsPerPage = 10;
  currentPage = 1;
  totalPages = 0;
  pages: number[] = [];

  updatePagination() {
    this.totalPages = Math.ceil(this.filteredProjects.length / this.cardsPerPage);
    this.pages = Array.from({length: this.totalPages}, (_, i) => i + 1);
  }

  get pagedCards() {
    const start = (this.currentPage - 1) * this.cardsPerPage;
    return this.filteredProjects.slice(start, start + this.cardsPerPage);
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  goToPrevious() {
    if (this.currentPage > 1) this.currentPage--;
  }

  goToNext() {
    if (this.currentPage < this.totalPages) this.currentPage++;
  }

  selectChange(event: Event) {
    const numberSelected = event.target as HTMLSelectElement;
    if (numberSelected.value !== "All") {
      this.cardsPerPage = Number(numberSelected.value);
    } else {
      this.cardsPerPage = this.filteredProjects.length;
    }
    this.updatePagination();
  }

  showProject(event: Event) {
    const selection = event.target as HTMLSelectElement;
    const value = selection.value;

    if (value === 'All Projects') {
      this.filteredProjects = [...this.allProjects];
    } else if (value === 'Active') {
      this.filteredProjects = this.allProjects.filter(p => !p.isDeleted);
    } else if (value === 'Deleted') {
      this.filteredProjects = this.allProjects.filter(p => p.isDeleted);
    }

    this.currentPage = 1;
    this.updatePagination();
  }

  projectExportColumns = [
    {header: 'Title', field: 'title'},
    {header: 'School', field: 'school'},
    {header: 'Grades', field: 'grades'},
    {header: 'Description', field: 'projectDesc'}
  ];

  // export data in a csv file
  exportCSV(data: Project[], fileName: string) {
    if (!data || !data.length) return;
    this.downloadedTable = false;
    const rows: string[] = [];

    // headers
    rows.push(
      this.projectExportColumns
        .map(col => `"${col.header}"`)
        .join(',')
    );

    // rows
    data.forEach(project => {
      rows.push(
        this.projectExportColumns
          .map(col => {
            let val = (project as any)[col.field] ?? '';
            if (Array.isArray(val)) val = val.join(', ');
            return `"${String(val).replace(/"/g, '""')}"`
          })
          .join(',')
      );
    });

    const blob = new Blob([rows.join('\r\n')], {
      type: 'text/csv;charset=utf-8;'
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);

    this.downloadedTable = true;
  }

  sorts: { field: string; dir: 'asc' | 'desc' }[] = [];
  projectStatusOrder = ['New', 'In Progress', 'Completed', 'Rework','Abandoned'];
  completionStatusOrder = ['Not Started', 'In Progress', 'Completed'];

  sort(field: string) {
    const existing = this.sorts[0];

    if (existing?.field === field) {
      existing.dir = existing.dir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sorts = [{field, dir: 'asc'}];
    }

    this.applySorting();
  }

  isEmpty(val: any): boolean {
    return val === null ||
      val === undefined ||
      val === '' ||
      !String(val).trim();
  }

  selectedStatusSort: string | null = null;

  onStatusSortChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    this.selectedStatusSort = select.value || null;

    this.sorts = [{field: 'projectStatus', dir: 'asc'}];
    this.applySorting();
  }

  applySorting() {
    this.filteredProjects.sort((a, b) => {
      for (const s of this.sorts) {
        let aVal: any;
        let bVal: any;
        let result = 0;

        switch (s.field) {
          case 'school':
            aVal = a.school?.trim().toLowerCase();
            bVal = b.school?.trim().toLowerCase();
            break;

          case 'title':
            aVal = a.title?.trim().toLowerCase();
            bVal = b.title?.trim().toLowerCase();
            break;

          case 'teacher':
            aVal = a.representativeName?.trim().toLowerCase();
            bVal = b.representativeName?.trim().toLowerCase();
            break;

          case 'year':
            aVal = +(a.schoolYear?.split('-')[0] ?? 0);
            bVal = +(b.schoolYear?.split('-')[0] ?? 0);
            break;

          case 'date':
            aVal = new Date(a.dateSubmitted ?? 0).getTime();
            bVal = new Date(b.dateSubmitted ?? 0).getTime();
            break;

          case 'updatedDate':
            aVal = new Date(a.dateEdited ?? 0).getTime();
            bVal = new Date(b.dateEdited ?? 0).getTime();
            break;

          case 'completionStatus':
            aVal = this.rank(a.visibility, this.completionStatusOrder);
            bVal = this.rank(b.visibility, this.completionStatusOrder);
            break;

          case 'projectStatus':
            const selected = this.selectedStatusSort;

            aVal = a.projectStatus;
            bVal = b.projectStatus;

            if (selected) {
              if (aVal === selected && bVal !== selected) return -1;
              if (aVal !== selected && bVal === selected) return 1;
            }
            result =
              this.rank(aVal, this.projectStatusOrder) -
              this.rank(bVal, this.projectStatusOrder);
            break;
        }

        const aEmpty = this.isEmpty(aVal);
        const bEmpty = this.isEmpty(bVal);

        if (aEmpty && !bEmpty) return 1;
        if (!aEmpty && bEmpty) return -1;
        if (aEmpty && bEmpty) continue;

        if (typeof aVal === 'string') {
          result = aVal.localeCompare(bVal);
        } else {
          result = aVal - bVal;
        }

        if (result !== 0) {
          return s.dir === 'asc' ? result : -result;
        }
      }
      return 0;
    });

    this.updatePagination();
  }

  rank(value: string, order: string[]): number {
    const i = order.indexOf(value);
    return i === -1 ? order.length : i;
  }

  getSortDir(field: string): 'asc' | 'desc' | null {
    const s = this.sorts.find(sort => sort.field === field);
    return s ? s.dir : null;
  }

  allProjects: Project[] = [];
}
